import { useState, useRef } from 'react';
import { clipsAPI } from '../lib/api';
import { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, Video, X, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const ClipCreator = ({ open, onOpenChange, onClipCreated }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [mediaType, setMediaType] = useState(null);
  const fileRef = useRef(null);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const isVideo = f.type.startsWith('video/');
    const isImage = f.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast.error('Please select an image or video file');
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error('File too large. Max 50MB');
      return;
    }

    setFile(f);
    setMediaType(isVideo ? 'video' : 'image');
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Select a photo or video');
      return;
    }
    setUploading(true);
    try {
      await clipsAPI.create(file, caption);
      toast.success('Clip posted!');
      handleClose();
      onClipCreated?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setCaption('');
    setMediaType(null);
    if (fileRef.current) fileRef.current.value = '';
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle>Create a Clip</DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-24 w-24 flex-col gap-2 rounded-2xl"
                onClick={() => { fileRef.current.accept = 'image/*'; fileRef.current.click(); }}
              >
                <Camera className="w-8 h-8" />
                <span className="text-xs">Photo</span>
              </Button>
              <Button
                variant="outline"
                className="h-24 w-24 flex-col gap-2 rounded-2xl"
                onClick={() => { fileRef.current.accept = 'video/*'; fileRef.current.click(); }}
              >
                <Video className="w-8 h-8" />
                <span className="text-xs">Video</span>
              </Button>
            </div>
            <p className="text-sm text-slate-gray">Max 60 seconds video or a photo. Expires in 48 hours.</p>
            <input ref={fileRef} type="file" className="hidden" onChange={handleFileSelect} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[400px] flex items-center justify-center">
              {mediaType === 'video' ? (
                <video src={preview} controls className="max-h-full max-w-full object-contain" />
              ) : (
                <img src={preview} alt="Preview" className="max-h-full max-w-full object-contain" />
              )}
              <button
                onClick={() => { setFile(null); setPreview(null); setMediaType(null); }}
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption..."
              rows={2}
              maxLength={280}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-gray">{caption.length}/280</span>
              <Button
                onClick={handleSubmit}
                disabled={uploading}
                className="rounded-full bg-dark-slate text-white hover:bg-slate-800"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Posting...' : 'Post Clip'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClipCreator;
